import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import { buildItemUrl } from "../utils/itemUrl";
import usePageTitle from "../hooks/usePageTitle";
import { API_BASE } from "../apiBase"; // ✅ unified API base

export default function EditItem() {
  usePageTitle("Edit Item");
  const { search } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const params = new URLSearchParams(search);
  const itemId = params.get("itemId");
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [editReason, setEditReason] = useState("");
  const [editImageFile, setEditImageFile] = useState(null);
  const [submitError, setSubmitError] = useState("");

  // 1) Fetch existing item data
  useEffect(() => {
    if (!itemId) return;
    axios
      .get(`${API_BASE}/item/${itemId}`)
      .then((r) => {
        const data = r.data;
        setFormData({
          name: data.name || "",
          brand: data.brand || "",
          category: data.category || "",
          year_released: data.year_released || "",
          team: data.team || "",
          player: data.player || "",
          character_name: data.character_name || "",
          set_name: data.set_name || "",
          series_collection: data.series_collection || "",
          language: data.language || "",
          number: data.number || "",
          edition_variant: data.edition_variant || "",
          artist: data.artist || "",
          item_type: data.item_type || "",
          secret_rarity: data.secret_rarity || "",
          is_signed: Boolean(data.is_signed),
          item_condition: data.item_condition || "Good",
          description: data.description || "",
          image_url: data.image_url || "",
        });
      })
      .catch(() => {
        alert("Unable to load item for editing");
        navigate(-1);
      })
      .finally(() => setLoading(false));
  }, [itemId, navigate]);

  const handleChange = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((f) => ({ ...f, [field]: val }));
  };

  const getSubmitErrorMessage = (err) => {
    const payload = err?.response?.data;
    if (!payload) return err?.message || "Failed to submit edits.";
    if (typeof payload === "string") return payload;
    return payload.message || payload.detail || payload.error || "Failed to submit edits.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError("");
    try {
      const proposedFields = { ...formData };
      const reason = editReason.trim() || "Full item edit submitted from item detail.";
      const endpoint = `${API_BASE}/api/items/${itemId}/submit-edit`;

      if (editImageFile) {
        const body = new FormData();
        body.append("proposed_json", JSON.stringify(proposedFields));
        body.append("reason", reason);
        body.append("image", editImageFile);
        await axios.post(endpoint, body, { withCredentials: true });
      } else {
        await axios.post(
          endpoint,
          {
            proposed_json: proposedFields,
            reason,
          },
          {
            withCredentials: true,
          }
        );
      }
      alert("Your edits have been submitted for review.");
      navigate(buildItemUrl({ item_id: itemId, name: formData?.name }));
    } catch (err) {
      console.error("submit edit failed", err?.response?.data || err);
      setSubmitError(getSubmitErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading || !formData) {
    return (
      <Box sx={{ textAlign: "center", p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{ maxWidth: 800, mx: "auto", px: isMobile ? 2 : 0, mt: 4, pb: 8 }}
      >
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom>
            Propose an Edit for "{formData.name}"
          </Typography>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Name */}
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={handleChange("name")}
                  fullWidth
                  required
                />
              </Grid>
              {/* Brand */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Brand"
                  value={formData.brand}
                  onChange={handleChange("brand")}
                  fullWidth
                />
              </Grid>
              {/* Category */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Category"
                  value={formData.category}
                  onChange={handleChange("category")}
                  fullWidth
                />
              </Grid>
              {/* Year Released */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Year Released"
                  type="number"
                  value={formData.year_released}
                  onChange={handleChange("year_released")}
                  fullWidth
                />
              </Grid>
              {/* Team */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Team"
                  value={formData.team}
                  onChange={handleChange("team")}
                  fullWidth
                />
              </Grid>
              {/* Player */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Player"
                  value={formData.player}
                  onChange={handleChange("player")}
                  fullWidth
                />
              </Grid>
              {/* Character */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Character"
                  value={formData.character_name}
                  onChange={handleChange("character_name")}
                  fullWidth
                />
              </Grid>
              {/* Set Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Set Name"
                  value={formData.set_name}
                  onChange={handleChange("set_name")}
                  fullWidth
                />
              </Grid>
              {/* Series */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Series / Collection"
                  value={formData.series_collection}
                  onChange={handleChange("series_collection")}
                  fullWidth
                />
              </Grid>
              {/* Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Number"
                  value={formData.number}
                  onChange={handleChange("number")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Language"
                  value={formData.language}
                  onChange={handleChange("language")}
                  fullWidth
                />
              </Grid>
              {/* Edition Variant */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Edition / Variant"
                  value={formData.edition_variant}
                  onChange={handleChange("edition_variant")}
                  fullWidth
                />
              </Grid>
              {/* Artist */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Artist / Illustrator"
                  value={formData.artist}
                  onChange={handleChange("artist")}
                  fullWidth
                />
              </Grid>
              {/* Item Type */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Item Type"
                  value={formData.item_type}
                  onChange={handleChange("item_type")}
                  fullWidth
                />
              </Grid>
              {/* Secret Rarity */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Secret Rarity"
                  value={formData.secret_rarity}
                  onChange={handleChange("secret_rarity")}
                  fullWidth
                />
              </Grid>
              {/* Signed */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_signed}
                      onChange={handleChange("is_signed")}
                    />
                  }
                  label="Signed"
                />
              </Grid>
              {/* Condition */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Condition</InputLabel>
                  <Select
                    value={formData.item_condition}
                    label="Condition"
                    onChange={handleChange("item_condition")}
                  >
                    {["New", "Like New", "Good", "Fair", "Poor"].map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={handleChange("description")}
                  multiline
                  rows={4}
                  fullWidth
                />
              </Grid>
              {/* Image URL */}
              <Grid item xs={12}>
                <TextField
                  label="Image URL"
                  value={formData.image_url}
                  onChange={handleChange("image_url")}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reason for edit"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Optional context for reviewers"
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" component="label">
                  Choose Edit Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => setEditImageFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {editImageFile && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {editImageFile.name}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{ borderRadius: "30px", py: 1.5 }}
              >
                Submit Edits
              </Button>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                sx={{ borderRadius: "30px", py: 1.5 }}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
}
